import React, { useState } from "react";
import { Tooltip } from "@material-ui/core";
import { TagAddBtn, TagChip, TagMoreChip, TagsCellWrap } from "../styles";
import { validateTag } from "../validators";

const MAX_VISIBLE = 3;
const MAX_TAGS = 8;

interface Props {
	tags: string[];
	onAdd: (tag: string) => void;
	onRemove: (tag: string) => void;
	onFilterByTag?: (tag: string) => void;
	readonly?: boolean;
}

export default function TagsCell({ tags, onAdd, onRemove, onFilterByTag, readonly }: Props) {
	const [inputVisible, setInputVisible] = useState(false);
	const [inputValue, setInputValue] = useState("");
	const [inputError, setInputError] = useState<string | null>(null);
	const [showAll, setShowAll] = useState(false);

	const visible = showAll ? tags : tags.slice(0, MAX_VISIBLE);
	const hiddenCount = tags.length - MAX_VISIBLE;

	function handleAddClick(e: React.MouseEvent) {
		e.stopPropagation();
		setInputVisible(true);
		setInputValue("");
		setInputError(null);
	}

	function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === "Enter") {
			commitTag();
		} else if (e.key === "Escape") {
			setInputVisible(false);
		}
	}

	function commitTag() {
		const tag = inputValue.trim().toLowerCase();
		const error = validateTag(tag);
		if (error) {
			setInputError(error);
			return;
		}
		if (tags.includes(tag)) {
			setInputVisible(false);
			return;
		}
		if (tags.length >= MAX_TAGS) {
			setInputError(`Max ${MAX_TAGS} tags per entry.`);
			return;
		}
		onAdd(tag);
		setInputVisible(false);
		setInputValue("");
	}

	function handleChipClick(e: React.MouseEvent, tag: string) {
		e.stopPropagation();
		onFilterByTag?.(tag);
	}

	function handleChipDelete(e: React.MouseEvent, tag: string) {
		e.stopPropagation();
		if (!readonly) onRemove(tag);
	}

	return (
		<TagsCellWrap onClick={(e) => e.stopPropagation()}>
			{visible.map((tag) => (
				<TagChip
					key={tag}
					label={tag}
					size="small"
					onClick={(e) => handleChipClick(e as React.MouseEvent, tag)}
					onDelete={readonly ? undefined : (e) => handleChipDelete(e as React.MouseEvent, tag)}
				/>
			))}
			{!showAll && hiddenCount > 0 && (
				<TagMoreChip
					onClick={(e) => {
						e.stopPropagation();
						setShowAll(true);
					}}>
					+{hiddenCount} more
				</TagMoreChip>
			)}
			{!readonly && tags.length < MAX_TAGS && !inputVisible && (
				<Tooltip
					title="Add tag"
					placement="top">
					<TagAddBtn onClick={handleAddClick}>+</TagAddBtn>
				</Tooltip>
			)}
			{inputVisible && (
				<Tooltip
					title={inputError || ""}
					open={!!inputError}
					placement="top">
					<input
						autoFocus
						value={inputValue}
						onChange={(e) => {
							setInputValue(e.target.value);
							setInputError(null);
						}}
						onKeyDown={handleInputKeyDown}
						onBlur={() => {
							commitTag();
							setInputVisible(false);
						}}
						placeholder="tag-name"
						style={{
							width: 80,
							fontSize: "0.75rem",
							padding: "2px 6px",
							border: `1px solid ${inputError ? "#dc2626" : "#94a3b8"}`,
							borderRadius: 4,
							fontFamily: "inherit",
						}}
					/>
				</Tooltip>
			)}
		</TagsCellWrap>
	);
}
